import { ListingResource, RoomField } from '../../system/field';
import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { useReso } from '../../contexts/ResoContext';

export class RoomResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[RoomField.ROOM_KEY];
  }

  getItemNumber(): number | undefined {
    return this[RoomField.ROOM_ITEM_NUMBER];
  }

  getListingKeyField(): string {
    return RoomField.ROOM_LISTING_KEY;
  }

  getListingKey(): string | undefined {
    return this[RoomField.ROOM_LISTING_KEY];
  }

  // Utility methods for room resources

  /**
   * Get the room type
   */
  getType(): string | null {
    return this[RoomField.ROOM_TYPE] || null;
  }

  /**
   * Get the room level
   */
  getLevel(): string | null {
    return this[RoomField.ROOM_LEVEL] || null;
  }

  /**
   * Get the room dimensions as a formatted string (length x width)
   */
  getDimensions(): string | null {
    if (this[RoomField.ROOM_DIMENSIONS]) {
      return this[RoomField.ROOM_DIMENSIONS];
    }

    const length = this[RoomField.ROOM_LENGTH];
    const width = this[RoomField.ROOM_WIDTH];

    if (length && width) {
      return `${length} x ${width}`;
    }

    return null;
  }

  /**
   * Get the room area
   */
  getArea(): number | null {
    if (
      this[RoomField.ROOM_AREA] !== undefined &&
      this[RoomField.ROOM_AREA] !== null
    ) {
      return Number(this[RoomField.ROOM_AREA]);
    }

    const length = this[RoomField.ROOM_LENGTH];
    const width = this[RoomField.ROOM_WIDTH];

    if (length && width) {
      return length * width;
    }

    return null;
  }

  /**
   * Get the room features as an array
   */
  getFeatures(): string[] {
    const features = this[RoomField.ROOM_FEATURES];
    if (!features) return [];

    if (Array.isArray(features)) {
      return features;
    }

    if (typeof features === 'string') {
      return features.split(',').map((feature) => feature.trim());
    }

    return [];
  }

  /**
   * Get the room description
   */
  getDescription(): string | null {
    return this[RoomField.ROOM_DESCRIPTION] || null;
  }

  /**
   * Get the display order
   */
  getDisplayOrder(): number | undefined {
    return this[RoomField.ROOM_DISPLAY_ORDER];
  }

  /**
   * Check if the room exists
   */
  doesExist(): boolean {
    return this[RoomField.ROOM_EXISTS_YN] !== false;
  }
}

export class RoomResourceManager extends BaseResourceManager<RoomResource> {
  constructor(
    resourceType: ListingResource,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super(resourceType, executeQuery, fieldTranslator, taskManager, options);
  }

  /**
   * Core mapping function from API data to RoomResource
   */
  protected mapFromApiCore(apiData: any): RoomResource {
    return new RoomResource(apiData);
  }

  /**
   * Core mapping function from RoomResource to API data
   */
  protected mapToApiCore(resource: RoomResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Filter rooms by type
   */
  public async getByType(
    roomType: string,
    listingKey?: string,
  ): Promise<RoomResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByType' },
      async (taskId) => {
        try {
          // Get all rooms
          const allRooms = await this.list(listingKey);

          // Filter by room type
          return allRooms
            .filter((room) => room[RoomField.ROOM_TYPE] === roomType)
            .sort((a, b) => {
              // Sort by display order if available
              const orderA = a.getDisplayOrder() ?? 0;
              const orderB = b.getDisplayOrder() ?? 0;
              return orderA - orderB;
            });
        } catch (error) {
          console.error('Error getting rooms by type:', error);
          return [];
        }
      },
    );
  }

  /**
   * Filter rooms by level
   */
  public async getByLevel(
    level: string,
    listingKey?: string,
  ): Promise<RoomResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByLevel' },
      async (taskId) => {
        try {
          // Get all rooms
          const allRooms = await this.list(listingKey);

          // Filter by level
          return allRooms
            .filter((room) => room[RoomField.ROOM_LEVEL] === level)
            .sort((a, b) => {
              // Sort by display order if available
              const orderA = a.getDisplayOrder() ?? 0;
              const orderB = b.getDisplayOrder() ?? 0;
              return orderA - orderB;
            });
        } catch (error) {
          console.error('Error getting rooms by level:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get all bedroom types
   */
  public async getBedrooms(listingKey?: string): Promise<RoomResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getBedrooms' },
      async (taskId) => {
        try {
          // Get all rooms
          const allRooms = await this.list(listingKey);

          // Filter for bedroom types
          return allRooms
            .filter((room) =>
              room[RoomField.ROOM_TYPE]?.toLowerCase().includes('bedroom'),
            )
            .sort((a, b) => {
              // Sort by display order if available
              const orderA = a.getDisplayOrder() ?? 0;
              const orderB = b.getDisplayOrder() ?? 0;
              return orderA - orderB;
            });
        } catch (error) {
          console.error('Error getting bedrooms:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get all bathroom types
   */
  public async getBathrooms(listingKey?: string): Promise<RoomResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getBathrooms' },
      async (taskId) => {
        try {
          // Get all rooms
          const allRooms = await this.list(listingKey);

          // Filter for bathroom types
          return allRooms
            .filter((room) =>
              room[RoomField.ROOM_TYPE]?.toLowerCase().includes('bath'),
            )
            .sort((a, b) => {
              // Sort by display order if available
              const orderA = a.getDisplayOrder() ?? 0;
              const orderB = b.getDisplayOrder() ?? 0;
              return orderA - orderB;
            });
        } catch (error) {
          console.error('Error getting bathrooms:', error);
          return [];
        }
      },
    );
  }
}

/**
 * Create a new RoomResource with required fields
 */
export function createRoomResource(
  data: Partial<Record<string, any>> = {},
): RoomResource {
  const defaults = {
    [RoomField.ROOM_ITEM_NUMBER]: 0,
    [RoomField.ROOM_DISPLAY_ORDER]: 0,
    [RoomField.ROOM_EXISTS_YN]: true,
  };

  return new RoomResource({
    ...defaults,
    ...data,
  });
}
